import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { listSharesBulk } from '../services/sharing.service';
import type { ResourceType, Share } from '../services/sharing.service';

interface SharesContextType {
  sharesCache: Record<string, Share[]>;
  loadSharesBulk: (resourceType: ResourceType, resourceIds: number[]) => Promise<void>;
  getSharesForResource: (resourceType: ResourceType, resourceId: number) => Share[];
  clearCache: () => void;
}

const SharesContext = createContext<SharesContextType | undefined>(undefined);

export function SharesProvider({ children }: { children: ReactNode }) {
  const [sharesCache, setSharesCache] = useState<Record<string, Share[]>>({});

  const loadSharesBulk = useCallback(async (
    resourceType: ResourceType,
    resourceIds: number[]
  ) => {
    try {
      const results = await listSharesBulk(resourceType, resourceIds);

      setSharesCache((prev) => ({
        ...prev,
        ...Object.entries(results).reduce((acc, [id, shares]) => {
          acc[`${resourceType}:${id}`] = shares;
          return acc;
        }, {} as Record<string, Share[]>),
      }));
    } catch (error) {
      console.error('Erro ao carregar shares bulk:', error);
    }
  }, []);

  const getSharesForResource = useCallback((
    resourceType: ResourceType,
    resourceId: number
  ): Share[] => {
    const key = `${resourceType}:${resourceId}`;
    return sharesCache[key] || [];
  }, [sharesCache]);

  const clearCache = useCallback(() => {
    setSharesCache({});
  }, []);

  return (
    <SharesContext.Provider value={{ sharesCache, loadSharesBulk, getSharesForResource, clearCache }}>
      {children}
    </SharesContext.Provider>
  );
}

export function useShares() {
  const context = useContext(SharesContext);
  if (!context) {
    throw new Error('useShares must be used within SharesProvider');
  }
  return context;
}
